/**
 * LongTextR — multi-line text area.
 * Handles type: long_text
 */

import { useFormContext } from 'react-hook-form';
import { TextArea } from '../../../components/intake/fields';
import type { FieldDef, RendererProps } from '../types';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { buildValidationRules } from '../validation/fromSchema';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function LongTextR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);

  if (pdfMode) {
    const value = getValues(name);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">
          {value ?? <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  return (
    <TextArea
      name={name}
      label={label}
      hint={resolveHelpText(field, audience)}
      required={field.required}
      placeholder={field.example}
      rows={5}
      tooltip={lookupFieldTooltip(field.canonical_name)}
      rules={buildValidationRules(field)}
    />
  );
}
